/**
 * Hook for managing FehlzeitModal form state and logic.
 * 
 * Encapsulates all form state, validation, and handlers for the
 * absence creation/editing modal.
 * 
 * @module fehlzeiten/hooks/useFehlzeitModalForm
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useUserProfile } from '../../../../hooks/useUserProfile';
import { searchStudents, type StudentSearchResult } from '../api';
import type { FehlzeitEntry, AbsenceDuration, AbsenceSource, AbsenceStatus } from '../types';
import {
  formatDateDE,
  calculateDiffDays,
  countRecurringDates,
  buildAbsenceEntries,
  type BuildEntriesContext,
} from '../utils/fehlzeitFormUtils';

// ============================================================================
// Types
// ============================================================================

/** Form data structure */
export interface FehlzeitFormData {
  studentName: string;
  studentClass: string;
  courseClass: string;
  date: string;
  dateTo: string;
  timeFrom: string;
  timeTo: string;
  duration: AbsenceDuration;
  status: AbsenceStatus;
  reason: string;
  hasAttachment: boolean;
  hasAccess: boolean;
  sourceType: AbsenceSource;
  isExcusedLateness: boolean;
}

/** Partial day type options */
export type PartialDayType = 'window' | 'late_arrival' | 'leave_early';

/** Form validation errors */
export interface FormErrors {
  studentName?: string;
  date?: string;
  dateTo?: string;
  recurringUntil?: string;
  recurringWeekdays?: string;
  timeRange?: string;
  limit?: string;
}

/** A student to pre-select when opening the modal for a NEW entry. */
export interface PrefillStudent {
  id: string;
  name: string;
  className: string;
}

/** Hook parameters */
export interface UseFehlzeitModalFormParams {
  isOpen: boolean;
  editingEntry: FehlzeitEntry | null;
  /** When set (and not editing), the modal opens with this student
   *  already selected — used by the Schnelleingabe-from-a-row flow. */
  prefillStudent?: PrefillStudent | null;
}

/** Hook return type */
export interface UseFehlzeitModalFormReturn {
  // Form data
  formData: FehlzeitFormData;
  setFormData: React.Dispatch<React.SetStateAction<FehlzeitFormData>>;

  // Mode states
  isFullDay: boolean;
  isMultipleDays: boolean;
  isRecurring: boolean;
  recurringUntil: string;
  recurringWeekdays: number[];
  partialDayType: PartialDayType;

  // Student search
  studentSearch: string;
  setStudentSearch: (value: string) => void;
  showStudentSuggestions: boolean;
  setShowStudentSuggestions: (value: boolean) => void;
  selectedStudentId: string | null;
  setSelectedStudentId: (id: string | null) => void;
  suggestions: StudentSearchResult[];
  searching: boolean;

  // File upload
  uploadedFile: File | null;
  setUploadedFile: (file: File | null) => void;

  // Validation
  errors: FormErrors;
  validateForm: () => boolean;
  isFormValid: boolean;

  // Computed values
  rangeWarning: string | null;
  diffDays: number;
  recurringCount: number;

  // Handlers
  handleDurationChange: (isFullDayChecked: boolean) => void;
  handleMultipleDaysChange: (isMultipleDaysChecked: boolean) => void;
  handleRecurringChange: (isRecurringChecked: boolean) => void;
  handleFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleStatusChange: (status: AbsenceStatus) => void;
  handleWeekdayToggle: (dayIndex: number) => void;
  handlePartialDayTypeChange: (type: PartialDayType) => void;
  setRecurringUntil: (value: string) => void;
  selectStudent: (student: StudentSearchResult) => void;

  // Utility
  formatDateDE: (iso: string) => string;
  buildEntries: () => (Omit<FehlzeitEntry, 'id' | 'createdBy' | 'createdAt' | 'lastEditedBy' | 'lastEditedAt'> & { _student_id?: string; _class_id?: string })[];
}

// ============================================================================
// Hook Implementation
// ============================================================================

const DEFAULT_FORM_DATA: FehlzeitFormData = {
  studentName: '',
  studentClass: '',
  courseClass: '',
  date: new Date().toISOString().split('T')[0],
  dateTo: '',
  timeFrom: '',
  timeTo: '',
  duration: 'Ganzer Tag',
  status: 'krankgemeldet',
  reason: '',
  hasAttachment: false,
  hasAccess: true,
  sourceType: 'schuleingabe',
  isExcusedLateness: false,
};

/**
 * Custom hook for managing FehlzeitModal form state.
 */
export function useFehlzeitModalForm(
  params: UseFehlzeitModalFormParams
): UseFehlzeitModalFormReturn {
  const { isOpen, editingEntry, prefillStudent } = params;
  const { userProfile } = useUserProfile();

  // Form data state
  const [formData, setFormData] = useState<FehlzeitFormData>(DEFAULT_FORM_DATA);

  // Mode states
  const [isFullDay, setIsFullDay] = useState(true);
  const [isMultipleDays, setIsMultipleDays] = useState(false);
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringUntil, setRecurringUntil] = useState('');
  const [recurringWeekdays, setRecurringWeekdays] = useState<number[]>([1]);
  const [partialDayType, setPartialDayType] = useState<PartialDayType>('leave_early');

  // Student search state
  const [studentSearch, setStudentSearch] = useState('');
  const [showStudentSuggestions, setShowStudentSuggestions] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<StudentSearchResult[]>([]);
  const [searching, setSearching] = useState(false);

  // File upload
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  // Validation
  const [errors, setErrors] = useState<FormErrors>({});
  const [rangeWarning, setRangeWarning] = useState<string | null>(null);

  // Initialize form when opening or editing
  useEffect(() => {
    if (editingEntry) {
      const timeRange = editingEntry.dateRange?.split('–') || [];
      const startDate = editingEntry.date.split('.').reverse().join('-');
      const endDate = editingEntry.endDate ? editingEntry.endDate.split('.').reverse().join('-') : '';

      setFormData({
        studentName: editingEntry.studentName,
        studentClass: editingEntry.studentClass,
        courseClass: editingEntry.courseClass,
        date: startDate,
        dateTo: endDate,
        timeFrom: timeRange[0]?.trim() || '',
        timeTo: timeRange[1]?.trim() || '',
        duration: editingEntry.duration,
        status: editingEntry.status,
        reason: editingEntry.reason,
        hasAttachment: editingEntry.hasAttachment,
        hasAccess: editingEntry.hasAccess,
        sourceType: editingEntry.sourceType,
        isExcusedLateness: editingEntry.isExcusedLateness || false,
      });
      setIsFullDay(editingEntry.duration === 'Ganzer Tag');

      if (editingEntry.duration !== 'Ganzer Tag') {
        const [from, to] = editingEntry.dateRange?.split('–') || [];
        const f = from?.trim();
        const t = to?.trim();
        if (f && !t) setPartialDayType('leave_early');
        else if (!f && t) setPartialDayType('late_arrival');
        else setPartialDayType('window');
      }

      setIsMultipleDays(!!editingEntry.isMultiDay);
      setIsRecurring(false);
      setRecurringUntil('');
      setRecurringWeekdays([1]);
      setStudentSearch(editingEntry.studentName);

      if (editingEntry.status === 'verspätet') {
        setIsFullDay(false);
        setIsMultipleDays(false);
      }
    } else {
      // New entry. When `prefillStudent` is set (Schnelleingabe opened
      // from a specific student's row), seed that student so the
      // secretary only has to fill the reason/dates.
      setFormData({
        ...DEFAULT_FORM_DATA,
        date: new Date().toISOString().split('T')[0],
        studentName: prefillStudent?.name ?? '',
        studentClass: prefillStudent?.className ?? '',
      });
      setIsFullDay(true);
      setPartialDayType('leave_early');
      setIsMultipleDays(false);
      setIsRecurring(false);
      setRecurringUntil('');
      setRecurringWeekdays([1]);
      setStudentSearch(prefillStudent?.name ?? '');
      setUploadedFile(null);
      setSelectedStudentId(prefillStudent?.id ?? null);
      setShowStudentSuggestions(false);
      setErrors({});
    }
  }, [editingEntry, isOpen, prefillStudent]);

  // Student search effect
  useEffect(() => {
    let active = true;
    const handler = setTimeout(async () => {
      if (!studentSearch || studentSearch.trim().length < 2 || !userProfile?.school_id) {
        if (active) setSuggestions([]);
        return;
      }
      // Don't re-search while the box still shows the already-selected
      // student's name — avoids a suggestion dropdown flashing open
      // right after a prefill.
      if (selectedStudentId && studentSearch === formData.studentName) {
        if (active) setSuggestions([]);
        return;
      }
      setSearching(true);
      try {
        const results = await searchStudents(studentSearch, userProfile.school_id);
        if (active) {
          setSuggestions(results);
          if (results.length) setShowStudentSuggestions(true);
        }
      } catch {
        if (active) setSuggestions([]);
      } finally {
        if (active) setSearching(false);
      }
    }, 250);
    return () => {
      active = false;
      clearTimeout(handler);
    };
  }, [studentSearch, userProfile?.school_id, selectedStudentId, formData.studentName]);

  // Range warning effect
  useEffect(() => {
    if (isMultipleDays && formData.date && formData.dateTo) {
      const start = new Date(formData.date);
      const end = new Date(formData.dateTo);
      const msPerDay = 24 * 60 * 60 * 1000;
      const diffDays = Math.floor((end.getTime() - start.getTime()) / msPerDay) + 1;
      if (diffDays > 10) {
        setRangeWarning(`Hinweis: Der ausgewählte Zeitraum umfasst ${diffDays} Tage. Bitte prüfen, ob das korrekt ist (max. empfohlen: 10 Tage).`);
      } else {
        setRangeWarning(null);
      }
    } else {
      setRangeWarning(null);
    }
  }, [isMultipleDays, formData.date, formData.dateTo]);

  // Computed values - use extracted utilities
  const diffDays = useMemo(() => {
    if (isMultipleDays && formData.date && formData.dateTo) {
      return calculateDiffDays(formData.date, formData.dateTo);
    }
    return 0;
  }, [isMultipleDays, formData.date, formData.dateTo]);

  const recurringCount = useMemo(() => {
    if (isRecurring && formData.date && recurringUntil && recurringWeekdays.length) {
      return countRecurringDates(formData.date, recurringUntil, recurringWeekdays);
    }
    return 0;
  }, [isRecurring, formData.date, recurringUntil, recurringWeekdays]);

  const isFormValid = useMemo(() => {
    const hasStudent = !!selectedStudentId || !!editingEntry;
    const hasDate = !!formData.date;
    const hasDateTo = !isMultipleDays || !!formData.dateTo;
    const hasRecurringConfig = !isRecurring || (!!recurringUntil && recurringWeekdays.length > 0);
    const hasTimeConfig = isFullDay || (
      partialDayType === 'window'
        ? (!!formData.timeFrom && !!formData.timeTo)
        : (partialDayType === 'late_arrival' ? !!formData.timeTo : !!formData.timeFrom)
    );
    const hasLatenessTime = formData.status !== 'verspätet' || (!!formData.timeFrom || !!formData.timeTo);

    return hasStudent && hasDate && hasDateTo && hasRecurringConfig && hasTimeConfig && hasLatenessTime;
  }, [selectedStudentId, editingEntry, formData, isMultipleDays, isRecurring, recurringUntil, recurringWeekdays, isFullDay, partialDayType]);

  // Handlers
  const handleDurationChange = (isFullDayChecked: boolean) => {
    if (formData.status === 'verspätet') {
      setIsFullDay(false);
      setFormData(prev => ({ ...prev, duration: 'Zeitfenster' }));
      return;
    }
    setIsFullDay(isFullDayChecked);
    setFormData(prev => ({
      ...prev,
      duration: isFullDayChecked ? 'Ganzer Tag' : 'Zeitfenster',
      ...(isFullDayChecked ? { timeFrom: '', timeTo: '' } : {}),
    }));
  };

  const handleMultipleDaysChange = (isMultipleDaysChecked: boolean) => {
    if (formData.status === 'verspätet') return;
    setIsMultipleDays(isMultipleDaysChecked);
    if (isMultipleDaysChecked) {
      setIsRecurring(false);
      setRecurringUntil('');
    }
  };

  const handleRecurringChange = (isRecurringChecked: boolean) => {
    if (formData.status === 'verspätet') return;
    setIsRecurring(isRecurringChecked);
    if (isRecurringChecked) {
      setIsMultipleDays(false);
      setFormData(prev => ({ ...prev, dateTo: '' }));
    } else {
      setRecurringUntil('');
    }
  };

  const handleStatusChange = (status: AbsenceStatus) => {
    setFormData(prev => ({ ...prev, status }));
    if (status === 'verspätet') {
      setIsFullDay(false);
      setIsMultipleDays(false);
      setFormData(prev => ({
        ...prev,
        status,
        duration: 'Zeitfenster',
        dateTo: '',
        isExcusedLateness: false,
      }));
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    if (!file) return;
    const allowed = ['application/pdf', 'image/jpeg', 'image/png'];
    if (!allowed.includes(file.type)) {
      alert('Nur PDF, JPG und PNG Dateien sind erlaubt.');
      return;
    }
    setUploadedFile(file);
    setFormData(prev => ({ ...prev, hasAttachment: true }));
  };

  const handleWeekdayToggle = (dayIndex: number) => {
    setRecurringWeekdays(prev =>
      prev.includes(dayIndex)
        ? prev.filter(d => d !== dayIndex)
        : [...prev, dayIndex]
    );
  };

  const handlePartialDayTypeChange = (type: PartialDayType) => {
    setPartialDayType(type);
    if (type === 'leave_early') {
      setFormData(prev => ({ ...prev, timeTo: '' }));
    } else if (type === 'late_arrival') {
      setFormData(prev => ({ ...prev, timeFrom: '' }));
    }
  };

  const selectStudent = (student: StudentSearchResult) => {
    setSelectedStudentId(student.id);
    setFormData(prev => ({
      ...prev,
      studentName: student.name,
      studentClass: student.class_name || '',
    }));
    setStudentSearch(student.name);
    setShowStudentSuggestions(false);
  };

  const validateForm = (): boolean => {
    const e: FormErrors = {};
    const isEditing = !!editingEntry;
    if (!selectedStudentId && !isEditing) e.studentName = 'Bitte Schüler*in wählen';
    if (!formData.date) e.date = 'Datum ist erforderlich';
    if (isMultipleDays && !formData.dateTo) e.dateTo = 'End-Datum ist erforderlich';
    if (isMultipleDays && formData.dateTo && formData.date) {
      const start = new Date(formData.date);
      const end = new Date(formData.dateTo);
      if (end < start) e.dateTo = 'End-Datum muss nach Start-Datum liegen';
      else if (end.getTime() === start.getTime()) e.dateTo = 'Für einen einzigen Tag "Mehrere Tage" nicht nutzen';
    }
    if (isRecurring) {
      if (!recurringUntil) e.recurringUntil = 'End-Datum ist erforderlich';
      if (!recurringWeekdays.length) e.recurringWeekdays = 'Bitte mindestens einen Wochentag wählen';
      if (recurringUntil && formData.date) {
        const s = new Date(formData.date);
        const u = new Date(recurringUntil);
        if (u < s) e.recurringUntil = 'End-Datum muss nach Start-Datum liegen';
      }
    }
    if (!isFullDay) {
      if (partialDayType === 'late_arrival' && !formData.timeTo) e.timeRange = 'Ankunftszeit ist erforderlich';
      if (partialDayType === 'leave_early' && !formData.timeFrom) e.timeRange = 'Gehzeit ist erforderlich';
    }
    if (formData.status === 'verspätet' && (!formData.timeTo && !formData.timeFrom)) {
      e.timeRange = 'Für Verspätungen ist eine Zeitangabe erforderlich';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // formatDateDE is now imported from fehlzeitFormUtils

  const buildEntries = useCallback(() => {
    const context: BuildEntriesContext = {
      formData,
      isFullDay,
      isMultipleDays,
      isRecurring,
      recurringUntil,
      recurringWeekdays,
      selectedStudentId,
    };
    return buildAbsenceEntries(context);
  }, [formData, isFullDay, isMultipleDays, isRecurring, recurringUntil, recurringWeekdays, selectedStudentId]);

  return {
    // Form data
    formData,
    setFormData,

    // Mode states
    isFullDay,
    isMultipleDays,
    isRecurring,
    recurringUntil,
    recurringWeekdays,
    partialDayType,

    // Student search
    studentSearch,
    setStudentSearch,
    showStudentSuggestions,
    setShowStudentSuggestions,
    selectedStudentId,
    setSelectedStudentId,
    suggestions,
    searching,

    // File upload
    uploadedFile,
    setUploadedFile,

    // Validation
    errors,
    validateForm,
    isFormValid,

    // Computed values
    rangeWarning,
    diffDays,
    recurringCount,

    // Handlers
    handleDurationChange,
    handleMultipleDaysChange,
    handleRecurringChange,
    handleFileUpload,
    handleStatusChange,
    handleWeekdayToggle,
    handlePartialDayTypeChange,
    setRecurringUntil,
    selectStudent,

    // Utility
    formatDateDE,
    buildEntries,
  };
}
